<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { getInboxIconByType } from 'dashboard/helper/inbox';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import LiveChatCampaignDetails from './LiveChatCampaignDetails.vue';
import OneOffCampaignDetails from './OneOffCampaignDetails.vue';

const props = defineProps({
  campaignId: {
    type: Number,
    default: null,
  },
  title: {
    type: String,
    default: '',
  },
  message: {
    type: String,
    default: '',
  },
  isLiveChatType: {
    type: Boolean,
    default: false,
  },
  isEnabled: {
    type: Boolean,
    default: false,
  },
  status: {
    type: String,
    default: '',
  },
  sender: {
    type: Object,
    default: null,
  },
  inbox: {
    type: Object,
    default: null,
  },
  scheduledAt: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['edit', 'delete', 'start', 'pause', 'cancel']);

const { t } = useI18n();

const { formatMessage } = useMessageFormatter();

const STATUS_LABELS = {
  active:    () => t('CAMPAIGN.ONE_OFF.CARD.STATUS.SCHEDULED'),
  completed: () => t('CAMPAIGN.ONE_OFF.CARD.STATUS.COMPLETED'),
  running:   () => t('CAMPAIGN.ONE_OFF.CARD.STATUS.RUNNING'),
  error:     () => t('CAMPAIGN.ONE_OFF.CARD.STATUS.ERROR'),
  paused:    () => t('CAMPAIGN.ONE_OFF.CARD.STATUS.PAUSED'),
  cancelled: () => t('CAMPAIGN.ONE_OFF.CARD.STATUS.CANCELLED'),
};

const STATUS_COLORS = {
  active:    'text-n-teal-11',
  completed: 'text-n-slate-12',
  running:   'text-blue-600 dark:text-blue-400',
  error:     'text-red-600 dark:text-red-400',
  paused:    'text-amber-600 dark:text-amber-400',
  cancelled: 'text-n-slate-10',
};

const campaignStatus = computed(() => {
  if (props.isLiveChatType) {
    return props.isEnabled
      ? t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.ENABLED')
      : t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.DISABLED');
  }
  const s = props.status?.toLowerCase?.() || 'active';
  return STATUS_LABELS[s]?.() ?? s;
});

const statusTextColor = computed(() => {
  if (props.isLiveChatType) {
    return props.isEnabled ? 'text-n-teal-11' : 'text-n-slate-12';
  }
  const s = props.status?.toLowerCase?.() || 'active';
  return STATUS_COLORS[s] ?? 'text-n-slate-12';
});

const showStart  = computed(() => !props.isLiveChatType && ['active', 'paused'].includes(props.status?.toLowerCase?.()));
const showPause  = computed(() => !props.isLiveChatType && props.status?.toLowerCase?.() === 'running');
const showCancel = computed(() => !props.isLiveChatType && ['active', 'running', 'paused'].includes(props.status?.toLowerCase?.()));

const inboxName = computed(() => props.inbox?.name || '');

const inboxIcon = computed(() => {
  const { medium, channel_type: type } = props.inbox;
  return getInboxIconByType(type, medium);
});
</script>

<template>
  <CardLayout layout="row">
    <div class="flex flex-col items-start justify-between flex-1 min-w-0 gap-2">
      <div class="flex justify-between gap-3 w-fit">
        <span
          class="text-base font-medium capitalize text-n-slate-12 line-clamp-1"
        >
          {{ title }}
        </span>
        <span
          class="text-xs font-medium inline-flex items-center h-6 px-2 py-0.5 rounded-md bg-n-alpha-2"
          :class="statusTextColor"
        >
          {{ campaignStatus }}
        </span>
      </div>
      <div
        v-dompurify-html="formatMessage(message, false, false, false)"
        class="text-sm text-n-slate-11 line-clamp-1 [&>p]:mb-0 h-6"
      />
      <div class="flex items-center w-full h-6 gap-2 overflow-hidden">
        <LiveChatCampaignDetails
          v-if="isLiveChatType"
          :sender="sender"
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
        />
        <OneOffCampaignDetails
          v-else
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
          :scheduled-at="scheduledAt"
        />
      </div>
    </div>
    <div class="flex items-center justify-end gap-1.5 flex-shrink-0">
      <!-- Bulk dispatch controls -->
      <Button
        v-if="showStart"
        variant="faded"
        size="sm"
        color="teal"
        icon="i-lucide-play"
        :title="t('CAMPAIGN.ONE_OFF.CARD.ACTIONS.START')"
        @click="emit('start', campaignId)"
      />
      <Button
        v-if="showPause"
        variant="faded"
        size="sm"
        color="slate"
        icon="i-lucide-pause"
        :title="t('CAMPAIGN.ONE_OFF.CARD.ACTIONS.PAUSE')"
        @click="emit('pause', campaignId)"
      />
      <Button
        v-if="showCancel"
        variant="faded"
        size="sm"
        color="ruby"
        icon="i-lucide-x"
        :title="t('CAMPAIGN.ONE_OFF.CARD.ACTIONS.CANCEL')"
        @click="emit('cancel', campaignId)"
      />
      <!-- Edit (livechat) -->
      <Button
        v-if="isLiveChatType"
        variant="faded"
        size="sm"
        color="slate"
        icon="i-lucide-sliders-vertical"
        @click="emit('edit')"
      />
      <!-- Delete -->
      <Button
        variant="faded"
        color="ruby"
        size="sm"
        icon="i-lucide-trash"
        @click="emit('delete')"
      />
    </div>
  </CardLayout>
</template>
